import { useModal } from "@/Contexts";
import ErrorTooltip from "@/components/Form/ErrorTooltip";
import { StyledSelect } from "@/components/StyledSelect";
import { useTrans } from "@/translations";

import { useCallback, useRef } from "react";

import { throttle } from "lodash";
import { IdentityKey } from "server/coreApi/identities/types.js";

import { FieldType } from "./types";

type Props = {
  value: string;
  columnId: FieldType;
  setCellError: (error: string) => void;
  errorMessage?: string;
  getAutocompleteOptions: (key: IdentityKey, value: string) => Promise<any>;
  onAutocompleteOptionSelected: (option: any) => void;
  disabled?: boolean;
  hidePlaceholder?: boolean;
};

export function SelectWithAutocomplete({
  value,
  columnId,
  errorMessage,
  setCellError,
  getAutocompleteOptions,
  onAutocompleteOptionSelected,
  disabled,
  hidePlaceholder,
}: Props) {
  const { t } = useTrans();
  const { ref: modalRef } = useModal();

  const updateWithAutocompleteDebounced = useRef(
    throttle(
      async (val: string, columnId: any, callback: (options: any) => void) => {
        const { options, hasError } = await getAutocompleteOptions(
          columnId,
          val,
        );

        const mapped = options.map((option: any) => ({
          label: option[columnId],
          value: option[columnId],
          ...option,
          error: hasError ? "errors:mustBeUnique" : "",
        }));

        callback(mapped);
      },
      500,
    ),
  );

  const loadOptions = useCallback(
    (inputValue: string, callback: (options: any) => void) => {
      updateWithAutocompleteDebounced.current(
        inputValue,
        columnId,
        (options) => {
          callback(options);
        },
      );
    },
    [columnId],
  );

  const onChange = useCallback(
    (option: any) => {
      onAutocompleteOptionSelected(option);
      if (option.error) {
        setCellError(option.error);
      }
    },
    [onAutocompleteOptionSelected, setCellError],
  );

  return (
    <div className="relative flex flex-col">
      <StyledSelect
        async
        className="m-1"
        loadOptions={loadOptions}
        value={value ? { label: value, value } : undefined}
        onChange={onChange}
        showErrorState={!!errorMessage}
        isDisabled={disabled}
        menuPortalTarget={modalRef?.current}
        noOptionsMessage={() => (
          <span className="float-left p-2 text-sm">
            {t("errors:noResults")}
          </span>
        )}
        placeholder={
          hidePlaceholder ? "" : t("genericLabels:selectPlaceholder")
        }
      />
      {errorMessage && (
        <ErrorTooltip
          error={{ type: "error", message: t(errorMessage) }}
          className="mt-1 mr-5"
        />
      )}
    </div>
  );
}
